import React from 'react';

import Paso1Info from './Paso1Info';
import CamposRecordatorios from './CamposRecordatorios';
import { contenidoParaUsuario } from './editarSecciones';

/**
 * BloquesCampana — la disposición común de Editar y del asistente (rediseño, Fases 2 y 3).
 * Solo presentación: reparte Paso1Info y CamposRecordatorios, que siguen siendo los que despachan.
 */

/**
 * «Por dentro» a la izquierda, «Lo que ve la persona» a la derecha y la vista previa debajo.
 *
 * Una rejilla POR FILAS (cabecera · título · descripción): cada fila mide lo que la más alta de sus dos
 * celdas, así que título queda con título y descripción con descripción aunque una ayuda ocupe más.
 *
 * El orden del DOM es el de MÓVIL (primero los tres bloques internos, luego los tres de la persona) y
 * en escritorio cada bloque va a su columna y fila EXPLÍCITAS (lg:col-start-* / lg:row-start-*). Sin
 * `order`: la versión anterior dependía de que `lg:order-none` ganara a `order-N` y en escritorio los
 * bloques salían desordenados.
 */
const CELDAS_INFORMACION = {
  cabeceraInterna: 'lg:col-start-1 lg:row-start-1',
  titulo: 'lg:col-start-1 lg:row-start-2',
  descripcion: 'lg:col-start-1 lg:row-start-3',
  cabeceraUsuario: 'lg:col-start-2 lg:row-start-1',
  tituloUsuario: 'lg:col-start-2 lg:row-start-2',
  descripcionUsuario: 'lg:col-start-2 lg:row-start-3',
};

export function InformacionDosColumnas({ payload, errores, dispatch, autoFocusTitulo = false }) {
  const p = { payload, errores, dispatch };
  const cabecera = 'text-xs font-medium uppercase tracking-wide text-v25-ink-500';
  const ayuda = 'mt-1 text-xs text-v25-ink-500';
  const c = CELDAS_INFORMACION;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-x-10 gap-y-5 lg:grid-cols-2 lg:grid-rows-[auto_auto_auto]" data-rejilla="informacion">
        <div className={c.cabeceraInterna} data-bloque="cabecera-interna">
          <h3 className={cabecera}>Por dentro</h3>
          <p className={ayuda}>Lo ven quienes gestionan la campaña y los técnicos.</p>
        </div>
        <div className={c.titulo} data-campo="titulo">
          <Paso1Info parte="titulo" autoFocusTitulo={autoFocusTitulo} {...p} />
        </div>
        <div className={c.descripcion} data-campo="descripcion">
          <Paso1Info parte="descripcion" {...p} />
        </div>
        <div className={`${c.cabeceraUsuario} mt-3 lg:mt-0`} data-bloque="cabecera-usuario">
          <h3 className={cabecera}>Lo que ve la persona</h3>
          <p className={ayuda}>En Mis Citas y en los avisos. Déjalo vacío para usar el título y la descripción internos.</p>
        </div>
        <div className={c.tituloUsuario} data-campo="titulo_usuario">
          <Paso1Info parte="titulo_usuario" {...p} />
        </div>
        <div className={c.descripcionUsuario} data-campo="descripcion_usuario">
          <Paso1Info parte="descripcion_usuario" {...p} />
        </div>
      </div>
      <VistaPreviaUsuario payload={payload} />
    </div>
  );
}

/** Asignación, cancelaciones y antelación en una fila; debajo, los dos recordatorios. */
export function BloqueReglas({ payload, errores, dispatch }) {
  return (
    <div className="space-y-6">
      <Paso1Info parte="reglas" payload={payload} errores={errores} dispatch={dispatch} />
      <div className="border-t border-v25-line pt-5">
        <h3 className="mb-2 text-xs font-medium text-slate-700">Recordatorios a quien no tenga cita</h3>
        <CamposRecordatorios compacto payload={payload} dispatch={dispatch} />
      </div>
    </div>
  );
}

// Cómo le aparecerá a la persona en Mis Citas: título y descripción efectivos (los suyos o, si están
// vacíos, los internos). Texto siempre como texto, nunca HTML.
export function VistaPreviaUsuario({ payload }) {
  const c = contenidoParaUsuario(payload);
  return (
    <div className="rounded-v25-md border border-dashed border-v25-line-strong bg-v25-surface-sunken p-4">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-v25-ink-500">Vista previa · así lo verá en Mis Citas</div>
      <div className="max-w-2xl rounded-v25-md border border-v25-line bg-white p-4">
        <div className="text-base font-semibold text-v25-ink-900 break-words">
          {c.titulo || <span className="font-normal italic text-v25-ink-400">Sin título</span>}
        </div>
        {c.descripcion ? (
          <p className="mt-1 whitespace-pre-line text-sm text-v25-ink-700 break-words">{c.descripcion}</p>
        ) : (
          <p className="mt-1 text-sm italic text-v25-ink-400">Sin descripción</p>
        )}
      </div>
      <p className="mt-2 text-[11px] text-v25-ink-500">
        {c.tituloEsInterno && c.descripcionEsInterna
          ? 'Como «Lo que ve la persona» está vacío, se usan el título y la descripción internos.'
          : c.tituloEsInterno
            ? 'El título para la persona está vacío: se usa el interno.'
            : c.descripcionEsInterna
              ? 'La descripción para la persona está vacía: se usa la interna.'
              : 'Se usan el título y la descripción para la persona.'}
      </p>
    </div>
  );
}
